import * as React from "react";
import { styled } from "@mui/material";
import { Flipped, Flipper } from "react-flip-toolkit";
import {
  DemoZoomContent,
  DemoZoomGraphButtonSupport,
  DemoZoomViewState,
} from "./useDemoReasoningController";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { MobileSuggestionReasoningBox } from "./mobileSuggestionReasoningBox";
import { MobileReasoningReferencesBox } from "./mobileReasoningReferencesBox";
import { MobileDiseaseReasoningBox } from "./mobileDiseaseReasoningBox";
import { scrollableStyleByColor } from "fe-common/ui/commonStyles";
import { PatientColorMap } from "../../../layout/patientColorMap";
import { DiseaseMatrices } from "fe-common/utils/zoomUtil";
import clsx from "clsx";
import { usePatient } from "../../../state/patientContext";
import { DemoModeGraphView } from "./demoModeGraphView";
import { TriageRecommendationReasoning } from "./triageRecommendationReasoning";
import { DemoModeGraphSelectionDetails } from "./demoModeGraphSelectionDetails";

//region [[ Styles ]]
const Content = styled("div")({
  display: "flex",
  flexDirection: "column",
  "&:not(.desktop)": {
    ...scrollableStyleByColor(PatientColorMap.Primary),
    maxHeight: "calc(100vh - 280px)",
  },
  // Give the scrollers some space from content
  padding: "0 15px",
});

const LoadingBox = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "50px 5px",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface DemoZoomContentSwitchProps
  extends DemoZoomContent,
    DemoZoomGraphButtonSupport {
  loadingMessage: string;
  currentView: DemoZoomViewState;
  onDiseaseInfoClick: (d: DiseaseMatrices) => void;
  desktop?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const DemoZoomContentSwitch = React.memo(
  ({
    currentView,
    loadingMessage,
    onDiseaseInfoClick,
    onReferencesClick,
    currentSelectedDisease,
    ...props
  }: DemoZoomContentSwitchProps) => {
    const { selectedSuggestion } = usePatient();

    const flipperKey =
      currentView + currentSelectedDisease?.diseaseId ||
      "" + selectedSuggestion?.qualifiedFeature?.id;

    return (
      <Flipper
        flipKey={flipperKey}
        spring={{ stiffness: 120, damping: 14 }}
        staggerConfig={{ default: { speed: 1 } }}
      >
        <Flipped
          key={`suggestion-zoom-content`}
          flipId={`inline-reasoning-content`}
        >
          <Content className={clsx("content", props.desktop ? "desktop" : "")}>
            {currentView === DemoZoomViewState.Loading && (
              <Flipper
                flipKey={loadingMessage}
                spring={{ stiffness: 120, damping: 14 }}
                staggerConfig={{ default: { speed: 1 } }}
              >
                <LoadingBox>
                  <Flipped
                    key={`loading_message`}
                    flipId={`loading_message`}
                    stagger
                  >
                    <span>{loadingMessage + " 💫"}</span>
                  </Flipped>
                  <ThreeDotsLoader />
                </LoadingBox>
              </Flipper>
            )}
            {currentView === DemoZoomViewState.SuggestionReasoning && (
              <MobileSuggestionReasoningBox
                onDiseaseSelected={onDiseaseInfoClick}
                onReferencesClick={onReferencesClick}
                setTitle={props.setTitle}
                desktop={props.desktop}
                onShowGraph={props.onShowGraph}
              />
            )}
            {currentView === DemoZoomViewState.References && (
              <MobileReasoningReferencesBox
                currentSelectedDisease={currentSelectedDisease}
                onReferencesClick={onReferencesClick}
                desktop={props.desktop}
              />
            )}
            {currentView === DemoZoomViewState.DiseaseReasoning && (
              <MobileDiseaseReasoningBox
                currentSelectedDisease={currentSelectedDisease}
                onReferencesClick={onReferencesClick}
                setTitle={props.setTitle}
                desktop={props.desktop}
                onShowGraph={props.onShowGraph}
              />
            )}
            {currentView === DemoZoomViewState.TriageReasoning && (
              <TriageRecommendationReasoning
                setTitle={props.setTitle}
                currentSelectedDisease={currentSelectedDisease}
                onReferencesClick={onReferencesClick}
                desktop={props.desktop}
              />
            )}
            {currentView === DemoZoomViewState.GraphView && (
              <DemoModeGraphView
                currentSelectedDisease={currentSelectedDisease}
                selectedFinding={props.selectedFinding}
                desktop={props.desktop}
                setTitle={props.setTitle}
              />
            )}
            {currentView === DemoZoomViewState.GraphDetails && (
              <DemoModeGraphSelectionDetails {...props} />
            )}
          </Content>
        </Flipped>
      </Flipper>
    );
  },
);
